import { Alert, Avatar, Input, List, Modal, message } from "antd";
import { useEffect, useState } from "react";
import { ERROR_CODE, RELOAD_ERROR_CODES } from "features/ConsultRequest/core/constants";
import { assignConsultRequest, listAssignees } from "features/ConsultRequest/data/apiAdapters";
import { useConsultCall } from "features/ConsultRequest/data/hooks";

/**
 * Popup Phân công / Đổi người. request = { id, version }.
 * onAssigned(detail, name): 200 -> nơi gọi thay Detail hoặc tải lại danh sách.
 * onStale(): STATE_CHANGED/LOCKED/FORBIDDEN -> nơi gọi tải lại (PW4 ở chi tiết, PW1 ở danh sách).
 */
export default function AssignModal({ visible, request, onClose, onAssigned, onStale }) {
  const { call } = useConsultCall();
  const [keyword, setKeyword] = useState("");
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!visible) return undefined;
    setLoading(true);
    const timer = setTimeout(() => {
      call(listAssignees, keyword.trim())
        .then((items) => {
          setOptions(items || []);
          setError(null);
        })
        .catch((e) => setError(e))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [visible, keyword, call]);

  useEffect(() => {
    if (visible) {
      setSelected(null);
      setKeyword("");
      setError(null);
    }
  }, [visible]);

  const submit = async () => {
    setSaving(true);
    try {
      const detail = await call(assignConsultRequest, request.id, {
        assigneeId: selected.userId,
        version: request.version,
      });
      message.success(`Đã phân công cho ${selected.name}`);
      onAssigned?.(detail, selected.name);
      onClose();
    } catch (e) {
      if (e.code === ERROR_CODE.VALIDATION_ERROR) {
        setError(e);
      } else {
        message.error(e.message);
        if (RELOAD_ERROR_CODES.includes(e.code)) {
          onStale?.();
          onClose();
        }
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Phân công kỹ thuật viên"
      visible={visible}
      onCancel={onClose}
      onOk={submit}
      okText="Phân công"
      cancelText="Hủy"
      okButtonProps={{ disabled: !selected, loading: saving }}
      destroyOnClose
    >
      <Input.Search
        allowClear
        placeholder="Tìm theo tên"
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        style={{ marginBottom: 12 }}
      />
      {error && (
        <Alert type="error" message={error.errors?.[0]?.message || error.message} style={{ marginBottom: 12 }} />
      )}
      <List
        loading={loading}
        dataSource={options}
        locale={{ emptyText: "Không có nhân viên phù hợp" }}
        style={{ maxHeight: 360, overflow: "auto" }}
        renderItem={(item) => (
          <List.Item
            onClick={() => setSelected(item)}
            style={{
              cursor: "pointer",
              background: selected?.userId === item.userId ? "#EEF3FF" : undefined,
              padding: 8,
            }}
          >
            <List.Item.Meta
              avatar={<Avatar src={item.avatarUrl}>{item.name?.[0]}</Avatar>}
              title={item.name}
              description={`${item.activeCount} phiếu đang xử lý`}
            />
          </List.Item>
        )}
      />
    </Modal>
  );
}
